<template>
    <div class="splash-card centered" v-if="loading"><be-spinner/></div>

    <div class="splash-card" v-else-if="!assessment.isCheckCompleted">
        <be-alert border icon-colored icon="close" color="danger">
            <strong>Digital.Check ausstehend</strong>
            <p>Führen Sie zuerst den Digital.Check durch. Danach können Sie unter diesem Punkt das Digital.Assessment dazu ausfüllen.</p>
            <router-link :to="{ name: 'assessment_check' }" class="btn btn-primary">Zum Digital.Check</router-link>
        </be-alert>
    </div>

    <div class="splash-card" v-else-if="!started && !assessment.isAssessmentCompleted">
        <be-alert border icon-colored icon="check" color="success">
            <strong>Digital.Assessment bearbeiten</strong>
            <p>Wollen Sie das Assessment jetzt bearbeiten? Bei Klick auf <em>Weiter</em> oder einem Menüpunkt werden Ihre Antworten automatisch gespeichert.</p>
            <button type="button" class="btn btn-primary" @click.prevent="loadAssessment">Assessment starten</button>
        </be-alert>
    </div>

    <div class="splash-card" v-else-if="questions === false">
        <be-alert border icon="alert-triangle" color="danger">
            <strong>Digital.Assessment Fehler</strong>
            <p>Für dieses Assessment oder diese Branche wurde kein Fragenkatalog konfiguriert.</p>
        </be-alert>
    </div>

    <be-loading :active="submitting" v-else>
        <df-exam ref="exam" :steps="steps" :disabled="assessment.isAssessmentCompleted" :input="formData" @started="onStarted" @progress="onProgress" @complete="onComplete">
            <be-spinner v-if="!completed"/>
            <div class="d-flex flex-column align-items-center my-5" v-else>
                <be-alert border icon-background color="success" icon="check" style="max-width:600px">
                    <strong>Digital.Assessment erfolgreich abgeschlossen</strong>
                    <p>Bitte füllen Sie basierend auf den Erkenntnissen dieses Assessments den Report mit den Handlungsempfehlungen aus.</p>
                    <router-link :to="{ name: 'assessment_report' }" class="btn btn-primary">Digital.Report erstellen</router-link>
                </be-alert>
            </div>
        </df-exam>
    </be-loading>
</template>

<script>
    import Vue from 'vue';

    import errorHandler from '../../../mixins/errorHandler';
    import Assessment from '../../../models/Assessment';
    import AssessmentQuestions from '../../../data/AssessmentQuestions';
    import FrameworkCategories from '../../../data/FrameworkCategories';

    import DfExam from '../../blocks/DfExam';
    import SurveyPage from './survey/Page';

    export default {
        mixins: [errorHandler],
        components: { DfExam },

        props: {
            assessment: Assessment,
        },

        data: () => ({
            loading: false,
            submitting: false,
            started: false,
            completed: false,

            questions: {},
            formData: null,

            steps: [],
        }),

        methods: {
            async loadAssessment() {
                this.loading = true;

                const questions = AssessmentQuestions.getForSector(this.assessment.sector);
                const categories = FrameworkCategories.all();
                const answers = await this.assessment.getAssessmentAnswers();

                const questionsByCategory = {};

                if (!(await questions).length) {
                    this.started = true;
                    this.loading = false;
                    this.questions = false;
                    return;
                }

                (await questions).forEach((question) => {
                    if (this.assessment.isAssessmentCompleted
                        && answers.length
                        && undefined === answers.find(a => a.assessmentQuestion === question.resourceId)
                    ) {
                        return;
                    }

                    if (!questionsByCategory[question.frameworkCategory]) {
                        questionsByCategory[question.frameworkCategory] = [];
                    }

                    questionsByCategory[question.frameworkCategory].push(question);
                });

                this.questions = questionsByCategory;

                this.formData = null;

                if (answers.length) {
                    this.formData = {};

                    answers.forEach((answer) => {
                        this.formData[answer.assessmentQuestion] = {
                            score: Assessment.scoreFromApi[answer.score],
                            notes: answer.notes,
                        };
                    });
                }

                this.steps = [];
                (await categories).forEach((category) => {
                    const formData = {};

                    this.questions[category.resourceId].forEach((question) => {
                        formData[question.resourceId] = {
                            score: Assessment.defaultScores.mid,
                            notes: '',
                        };
                    });

                    this.steps.push({
                        label: category.title,
                        component: Vue.extend({
                            mixins: [SurveyPage],
                            data: () => ({
                                category,
                                questions: this.questions[category.resourceId],
                                formData,
                            }),
                        }),
                    });
                });

                if (!this.assessment.isAssessmentCompleted) {
                    this.steps.push({
                        label: 'Digital.Report',
                    });
                }

                this.loading = false;
                this.started = true;
            },

            async save(formData, complete = false) {
                this.formData = formData;
                this.submitting = true;

                try {
                    const assessment = new Assessment({
                        id: this.assessment.id,
                    });

                    assessment.updateAnswers(Object.keys(formData).map(key => ({
                        assessmentQuestion: key,
                        score: Assessment.scoreToApi[formData[key].score],
                        notes: formData[key].notes,
                    })), complete);

                    this.$emit('update:assessment', await assessment.save());
                    this.completed = complete;
                    this.submitting = false;

                    if (!complete) {
                        this.$store.dispatch('notifications/add', {
                            color: 'success',
                            title: 'Digital.Assessment gespeichert',
                            description: 'Ihre Änderungen wurden erfolgreich zwischengespeichert.',
                        });
                    }
                } catch (response) {
                    this.catchResponseWithDialog(
                        'Fehler beim Übermitteln des Digital.Assessment',
                        this.assessment,
                        response,
                    );
                }
            },

            reset() {
                this.loading = false;
                this.submitting = false;
                this.started = false;
                this.completed = false;

                this.questions = {};
                this.formData = null;

                this.$v.$reset();
            },

            onStarted() {
                this.started = true;
            },

            onProgress(formData) {
                this.save(formData);
            },

            onComplete(formData) {
                this.save(formData, true);
            },
        },

        async beforeRouteLeave(to, from, next) {
            if (this.assessment.isAssessmentCompleted || !this.started || this.completed || this.questions === false) {
                next();
                return;
            }

            await this.save(this.$refs.exam.getFormData());
            next();
        },

        mounted() {
            if (this.assessment.isAssessmentCompleted) {
                this.loadAssessment();
            }
        },
    };
</script>
